import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { slideInFromRightWeighted } from '../../shared/component/Animation'
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtn } from '../../shared/component/headless/button/BtnBody';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { useProjectDetailState } from '../../shared/hooks/useApi/useProjectDetail';
import { useProjectInfoState } from '../../shared/hooks/useApi/useProjectInfo';
import { useProjectElementState } from '../../shared/hooks/useApi/useProjectElement';
import DocumentProject from './DocumentProject';
import { pdf } from '@react-pdf/renderer';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { WorkViewerInfo } from '../../shared/component/headless/input/InputBody';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { AboutContentMode, TextAlignment } from '../../shared/enum/EnumRepository';
import { PdfImageQuality, resolveProjectImageUrls } from '../../shared/util/pdfImageUtils';
import { paperTextureSmall } from '../../shared/component/paperTexture';
import { UniversalCheckbox } from '../../shared/component/UniversalCheckbox';
import { getMemberInfo } from '../../shared/api/memberInfoApi';
import { getCareerList } from '../../shared/api/careerApi';
import { getAboutPageSetting } from '../../shared/api/aboutPageSettingApi';
import { MemberInfoData, CareerData } from '../../shared/dto/EntityRepository';
import { normalizeCareerList } from '../../shared/util/normalizeCareer';
import { normalizeCareerSectionOrder } from '../../shared/util/careerSectionOrder';
import { runCurrentArchiveRequest } from '../../shared/util/currentArchiveRequest';
import { useSettingStore } from '../../shared/store/settingStore';
import { shouldShowWorkArtist } from '../../shared/util/workArtist';

const PDFProjectStationModal: React.FC = () => {
  const { isClosing, clearModal } = useModalStore();
  const { project } = useProjectDetailState();
  const { projectInfoList } = useProjectInfoState();
  const { projectElementList } = useProjectElementState();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const setting = useSettingStore((state) => state.setting);
  const includeArtist = shouldShowWorkArtist(setting);
  const [title, setTitle] = useState(project?.title || '');
  const [username, setUsername] = useState(pageOwner?.handle || '');
  const [withCV, setWithCV] = useState(false);
  const [contentMode, setContentMode] = useState<AboutContentMode>(AboutContentMode.Orig);
  const [quality, setQuality] = useState<PdfImageQuality>('MEDIUM');

  const [resolvedUrls, setResolvedUrls] = useState<{
    imageUrlMap: Record<string, string>;
    gifFirstFrameMap: Record<string, string>;
  } | null>(null);

  const listKey = useMemo(
    () => projectElementList?.map((e) => e.id).join(',') ?? '',
    [projectElementList]
  );
  const projectElementListRef = useRef(projectElementList);
  projectElementListRef.current = projectElementList;

  useEffect(() => {
    const list = projectElementListRef.current;
    setResolvedUrls(null);
    if (!list?.length) {
      setResolvedUrls({ imageUrlMap: {}, gifFirstFrameMap: {} });
      return;
    }
    let cancelled = false;
    resolveProjectImageUrls(list, quality).then((result) => {
      if (!cancelled) setResolvedUrls(result);
    });
    return () => {
      cancelled = true;
    };
  }, [listKey, quality]);

  const handleSubmit = async () => {
    if (!pageOwner) return;
    const { aui } = pageOwner;

    await runCurrentArchiveRequest(
      aui,
      async () => {
        const resolved =
          resolvedUrls ??
          (projectElementList?.length
            ? await resolveProjectImageUrls(projectElementList, quality)
            : { imageUrlMap: {}, gifFirstFrameMap: {} });

        let cvMemberInfo: MemberInfoData | null = null;
        let cvCareerList: CareerData[] = [];
        let careerSectionOrder = normalizeCareerSectionOrder(undefined);
        if (withCV) {
          try {
            const [miRes, clRes, aboutPageSettingRes] = await Promise.all([
              getMemberInfo(aui),
              getCareerList(aui),
              getAboutPageSetting(aui).catch(() => null),
            ]);
            cvMemberInfo = miRes.data;
            cvCareerList = normalizeCareerList(clRes.data ?? [])
              .filter((career) => career.isPublic);
            careerSectionOrder = normalizeCareerSectionOrder(
              aboutPageSettingRes?.data.careerSectionOrder,
            );
          } catch {
            cvMemberInfo = null;
            cvCareerList = [];
          }
        }

        const blob = await pdf(
          <DocumentProject
            title={title}
            username={username}
            includeArtist={includeArtist}
            projectInfoList={projectInfoList}
            projectElementList={projectElementList}
            resolvedImageUrlMap={resolved.imageUrlMap}
            resolvedGifFirstFrameMap={resolved.gifFirstFrameMap}
            includeCv={withCV}
            cvContentMode={contentMode}
            cvMemberInfo={cvMemberInfo}
            cvCareerList={cvCareerList}
            careerSectionOrder={careerSectionOrder}
          />
        ).toBlob();

        return {
          blob,
          fileName:
            withCV && contentMode === AboutContentMode.ENG
              ? `Project_${title}_CV_EN.pdf`
              : `Project_${title}.pdf`,
        };
      },
      ({ blob, fileName }) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
        clearModal(400);
      },
    );
  };

  return (
    <LeftModalFrame
      onClick={(e) => e.stopPropagation()}
      $isClosing={isClosing}
    >
      <PDFProjectStationComp>
        <Title>{getModalMessage('PDF_PROJECT_OPTIONS_TITLE')}</Title>

          <PDFInfo>
            <PDFInfoRow>
              <PDFInfoLabel>Title</PDFInfoLabel>
              <HeadlessInput
                value={title}
                placeholder="Title"
                handleChange={(e) => setTitle(e.target.value)}
                StyledInput={WorkViewerInfo}
                $alignment={TextAlignment.LEFT}
              />
            </PDFInfoRow>
            <PDFInfoRow>
              <PDFInfoLabel>Username</PDFInfoLabel>
              <HeadlessInput
                value={username}
                placeholder="Username"
                handleChange={(e) => setUsername(e.target.value)}
                StyledInput={WorkViewerInfo}
                $alignment={TextAlignment.LEFT}
              />
            </PDFInfoRow>
            <PDFInfoRow>
              <PDFInfoLabel>{getModalMessage('PDF_PROJECT_CV_LABEL')}</PDFInfoLabel>
              <RadioGroup>
                <CheckboxComp>
                  <UniversalCheckbox
                    type="radio"
                    name="pdfProjectCvInclude"
                    checked={withCV === false}
                    onChange={() => setWithCV(false)}
                  />
                  {getModalMessage('PDF_PROJECT_CV_WITHOUT')}
                </CheckboxComp>
                <CheckboxComp>
                  <UniversalCheckbox
                    type="radio"
                    name="pdfProjectCvInclude"
                    checked={withCV === true}
                    onChange={() => setWithCV(true)}
                  />
                  {getModalMessage('PDF_PROJECT_CV_WITH')}
                </CheckboxComp>
              </RadioGroup>
            </PDFInfoRow>
            {withCV && (
            <PDFInfoRow>
              <PDFInfoLabel>{getModalMessage('PDF_PROJECT_CV_LANGUAGE_LABEL')}</PDFInfoLabel>
              <RadioGroup>
                <CheckboxComp>
                  <UniversalCheckbox
                    type="radio"
                    name="pdfProjectCvLanguage"
                    checked={contentMode === AboutContentMode.Orig}
                    onChange={() => setContentMode(AboutContentMode.Orig)}
                  />
                  {getModalMessage('EXPORT_LANGUAGE_ORIGINAL')}
                </CheckboxComp>
                <CheckboxComp>
                  <UniversalCheckbox
                    type="radio"
                    name="pdfProjectCvLanguage"
                    checked={contentMode === AboutContentMode.ENG}
                    onChange={() => setContentMode(AboutContentMode.ENG)}
                  />
                  {getModalMessage('EXPORT_LANGUAGE_ENGLISH')}
                </CheckboxComp>
              </RadioGroup>
            </PDFInfoRow>
            )}
            <PDFInfoRow>
              <PDFInfoLabel>Quality</PDFInfoLabel>
              <RadioGroup>
                {(['LOW', 'MEDIUM', 'HIGH'] as PdfImageQuality[]).map((value) => (
                  <CheckboxComp key={value}>
                    <UniversalCheckbox
                      type="radio"
                      name="pdfProjectQuality"
                      checked={quality === value}
                      onChange={() => setQuality(value)}
                    />
                    {value[0] + value.slice(1).toLowerCase()}
                  </CheckboxComp>
                ))}
              </RadioGroup>
            </PDFInfoRow>
          </PDFInfo>

        <ButtonContainer>
          <HeadlessBtn
            value={getBtnName('GENERATE')}
            handleClick={handleSubmit}
            StyledBtn={BlackFitBtn}
          />
        </ButtonContainer>
      </PDFProjectStationComp>
    </LeftModalFrame>
  );
};

const LeftModalFrame = styled.div<{ $isClosing: boolean }>`
  position: absolute;
  right: clamp(20px, 2vw, 30px);
  bottom: clamp(70px, 6vw, 100px);
  width: 12vw;
  min-width: 320px;
  height: clamp(25vh, fit-content, 50vh);

  padding: 20px;
  
  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) =>
    $isClosing
      ? 'translateX(100%)'
      : 'translateX(0)'
  };

  transition: all 0.6s ease-out;
  ${paperTextureSmall}
  ${slideInFromRightWeighted}
`;

const PDFProjectStationComp = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: clamp(10px, 5vh, 30px);
`
const Title = styled.div`
  margin-bottom: 1.5vh;
  ${({ theme }) => theme.typography.Body_02_1};
`

const PDFInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1vh;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const PDFInfoRow = styled.div`
  min-height: 18px;
  margin-bottom: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const PDFInfoLabel = styled.div`
  min-width: 70px;
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin: 10px 0px;
`;


const RadioGroup = styled.div`
  display: flex;
  padding-top: 2px;
  gap: 1vw;
`;

const CheckboxComp = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;



export default PDFProjectStationModal;
